import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { mapsApi } from "../lib/api";

const STORAGE_KEY = "kt_location";
const LocationCtx = createContext(null);

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (Number.isFinite(v?.lat) && Number.isFinite(v?.lng)) return v;
  } catch { /* ignore */ }
  return null;
}

export function LocationProvider({ children }) {
  const [location, setLocationState] = useState(loadStored);
  const [locating, setLocating] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [initialDraft, setInitialDraft] = useState(null); // detected pin to fine-tune in the gate

  const setLocation = useCallback((loc) => {
    setLocationState(loc);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch { /* ignore */ }
  }, []);

  const detectPosition = useCallback((persist = true) => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("Geolocation unavailable")); return; }
    setLocating(true);
    const onOk = async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      let label = "Current location";
      try { const g = await mapsApi.reverse(lat, lng); if (g.address) label = g.address; } catch { /* ignore */ }
      const loc = { lat, lng, label };
      if (persist) setLocation(loc);
      setLocating(false);
      resolve(loc);
    };
    // Try a quick high-accuracy fix first; if it fails/times out (common on desktops and
    // IP-based fixes), retry with relaxed settings before giving up.
    navigator.geolocation.getCurrentPosition(
      onOk,
      () => navigator.geolocation.getCurrentPosition(
        onOk,
        (err) => { setLocating(false); reject(err); },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
      ),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  }), [setLocation]);

  const requestCurrentLocation = useCallback(() => detectPosition(true), [detectPosition]);

  // Always refresh to the live position on launch when the browser already has permission,
  // so the app reflects where the user is NOW instead of their last-used location. We never
  // force an unsolicited permission prompt here (only silent refresh when already granted).
  useEffect(() => {
    if (!navigator.geolocation || !navigator.permissions?.query) return;
    navigator.permissions.query({ name: "geolocation" })
      .then((st) => { if (st.state === "granted") detectPosition(true).catch(() => {}); })
      .catch(() => {});
  }, [detectPosition]);

  // Auto-detect on app start: if we already have a saved default, keep it; otherwise try the
  // device location, then open the gate pre-loaded with the detected pin so the customer can
  // FINE-TUNE it on the map before it becomes their default. If detection fails, open the
  // manual prompt so they can set it themselves.
  const ensureLocation = useCallback(async () => {
    if (location) return;
    if (!navigator.geolocation) { setInitialDraft(null); setPromptOpen(true); return; }
    try {
      const loc = await detectPosition(false);
      setInitialDraft(loc);
      setPromptOpen(true);
    } catch { setInitialDraft(null); setPromptOpen(true); }
  }, [location, detectPosition]);

  const value = {
    location, locating, setLocation, requestCurrentLocation, promptOpen, initialDraft,
    openPrompt: () => { setInitialDraft(null); setPromptOpen(true); },
    closePrompt: () => setPromptOpen(false), ensureLocation,
  };
  return <LocationCtx.Provider value={value}>{children}</LocationCtx.Provider>;
}

export function useLocationCtx() {
  const ctx = useContext(LocationCtx);
  if (!ctx) throw new Error("useLocationCtx must be used within LocationProvider");
  return ctx;
}
